import {
  type ExperienceConfiguration,
  useExperience
} from '@ninetailed/experience.js-react'
import { ExperienceMapper } from '@ninetailed/experience.js-utils'

import { useLocale } from '@ecomm/data-hooks'
import { getVisitorId } from '@ecomm/shared-cookies'
import { getRudderstack } from '@ecomm/shared-window'
import { useIntersectionObserver } from '@ecomm/web-apis/useIntersectionObserver'
import type { Map as ImmutableMap } from 'immutable'
import { useAtomValue } from 'jotai'
import React from 'react'
import { useEffect } from 'react'
import { ninetailedFeatureFlagsAtom } from '../ninetailedFeatureFlagsAtom'
import type { NinetailedFeatureFlagSchema } from './schema'

const getData = (data: NinetailedFeatureFlagSchema) => {
  return {
    id: data.sys.id,
    title: data.title,
    json: data.json,
    nt_experiences: getMappedExperiences(data)
  }
}

export const getMappedNinetailedFeatureFlag = (
  data: NinetailedFeatureFlagSchema
) =>
  (data.ntExperiencesCollection?.items || []).map(experience => ({
    id: experience.sys.id,
    name: experience.ntName,
    type: experience.ntType,
    config: experience.ntConfig,
    // This syntax accounts for the possibility of an audience not being set on an experiment
    ...(experience.ntAudience
      ? {
          audience: {
            name: experience.ntAudience.ntName,
            id: experience.ntAudience.sys.id
          }
        }
      : {}),
    variants: experience.ntVariantsCollection.items
      .filter(item => item.__typename === 'NtFeatureFlag')
      .map(variant => ({
        id: variant.sys.id, // Required
        title: variant.title,
        json: variant.json
      }))
  }))

const getMappedExperiences = (data: NinetailedFeatureFlagSchema) =>
  getMappedNinetailedFeatureFlag(data)
    .filter(experience => ExperienceMapper.isExperienceEntry(experience))
    .map(experience => ExperienceMapper.mapExperience(experience))

export type BaselineIdProps = {
  readonly baselineId: string
  readonly intersectionRef?: React.RefObject<HTMLElement>
}

export type ExperienceReturnStatus =
  | 'disabled'
  | 'error'
  | 'loading'
  | 'success'

export type FeatureFlag = {
  readonly data: {
    readonly id: string
    readonly title: string
    readonly json: { readonly id: string; readonly tag: string }
    readonly nt_experiences: readonly ExperienceConfiguration<{
      readonly id: string
      readonly title: string
      readonly json: { readonly id: string; readonly tag: string }
    }>[]
  }
  readonly experiences: ExperienceConfiguration<{
    readonly id: string
    readonly title: string
    readonly json: { readonly id: string; readonly tag: string }
  }>[]
  readonly baseline: { readonly id: string }
  readonly variantIndex: number
  readonly isControl: boolean
  readonly isVariant: boolean
  readonly status: ExperienceReturnStatus
}

/**
 * We return this if something is wrong with the contentful data, which is what happens if the entry isn't published yet.
 */
const emptyFeatureFlag: FeatureFlag = {
  data: {
    id: '',
    title: '',
    json: { id: '', tag: '' },
    nt_experiences: []
  },
  experiences: [],
  baseline: { id: '' },
  variantIndex: 0,
  isControl: true,
  isVariant: false,
  status: 'error'
}

export const useNinetailedFeatureFlag = ({
  baselineId,
  intersectionRef = React.createRef<HTMLElement>()
}: BaselineIdProps): FeatureFlag => {
  const locale = useLocale()
  const intersection = useIntersectionObserver(intersectionRef, {})

  const response: ImmutableMap<string, NinetailedFeatureFlagSchema> =
    useAtomValue(ninetailedFeatureFlagsAtom)
  const featureFlagStateValue = response.get(baselineId)

  if (!featureFlagStateValue) {
    return emptyFeatureFlag
  }

  const featureFlagData = getData(featureFlagStateValue)

  const experiences = getMappedExperiences(featureFlagStateValue)

  const maybeUseExperience =
    locale === 'en-US' &&
    useExperience({
      baseline: { id: baselineId },
      experiences
    })
  const variantIndex =
    (maybeUseExperience && maybeUseExperience.variantIndex) || 0
  const isControl = variantIndex === 0
  const variantName = isControl ? 'control' : `variant ${variantIndex}`
  const vid = getVisitorId()

  useEffect(() => {
    const { loading, status, variant, audience, experience } =
      maybeUseExperience || {}
    const validState =
      intersection?.isIntersecting &&
      !loading &&
      status === 'success' &&
      !!audience &&
      !!experience

    const payload = {
      ninetailed_component: variant?.id,
      ninetailed_experience: experience?.id,
      ninetailed_variant: variantName,
      vid
    }

    const fn = () => {
      getRudderstack(r => {
        r.track('nt_experience', payload)
      })
    }
    const id = validState && setTimeout(fn, 500)
    return () => {
      id ? clearTimeout(id) : void 0
    }
  }, [intersection, maybeUseExperience, variantName, vid])

  return {
    data: featureFlagData,
    experiences,
    baseline: { id: baselineId },
    variantIndex,
    isControl,
    isVariant: !isControl,
    status: maybeUseExperience ? maybeUseExperience.status : 'disabled'
  }
}
